const SANTIAGO_TZ = "America/Santiago";

const ASPECTO_LABELS: Record<string, string> = {
  trato_y_atencion: "Trato y atención",
  profesionales_y_resultado: "Profesionales y resultado",
  agendamiento_y_contacto: "Agendamiento y contacto",
  tiempos_de_espera: "Tiempos de espera",
  resultados_y_tramites: "Resultados y trámites",
  costos_y_cobros: "Costos y cobros",
  infraestructura_y_sedes: "Infraestructura y sedes",
  iniciativas_y_comunidad: "Iniciativas y comunidad",
  otro: "Otro",
};

export function aspectoLabel(value: string): string {
  const key = value.trim();
  if (!key) return "";
  if (ASPECTO_LABELS[key]) return ASPECTO_LABELS[key];
  const spaced = key.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function parseTimestamp(raw: string): Date | null {
  const iso = raw
    .trim()
    .replace(" ", "T")
    .replace(/(\.\d{3})\d+/, "$1")
    .replace(/\+00:00$/, "Z");
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
}

/** "08 oct 2026" en hora de Santiago. */
export function formatDateShort(raw: string): string {
  const d = parseTimestamp(raw);
  if (!d) return raw.slice(0, 10);
  const hasTime = raw.length > 10;
  return d.toLocaleDateString("es-CL", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: hasTime ? SANTIAGO_TZ : "UTC",
  });
}

/** "08 oct 2026, 10:57" en hora de Santiago. */
export function formatDateTime(raw: string): string {
  const d = parseTimestamp(raw);
  if (!d) return raw.slice(0, 16);
  if (raw.length <= 10) return formatDateShort(raw);
  const date = d.toLocaleDateString("es-CL", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: SANTIAGO_TZ,
  });
  const time = d.toLocaleTimeString("es-CL", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: SANTIAGO_TZ,
  });
  return `${date}, ${time}`;
}

const TRANSLATED_MARKER = /\(Translated by Google\)/i;
const ORIGINAL_MARKER = /\(Original\)/i;

/** Devuelve el texto original de una reseña GBP, sin la traducción automática de Google. */
export function stripGoogleTranslation(message: string): string {
  if (!message) return message;
  if (TRANSLATED_MARKER.test(message)) {
    const original = message.split(TRANSLATED_MARKER)[0].trim();
    if (original) return original;
  }
  if (ORIGINAL_MARKER.test(message)) {
    const parts = message.split(ORIGINAL_MARKER);
    const original = parts[parts.length - 1].trim();
    if (original) return original;
  }
  return message;
}

/** Primeras viñetas del reporte (sin filas de tabla), para el resumen colapsado. */
export function extractTeaser(markdown: string, max = 3): string[] {
  const out: string[] = [];
  for (const line of markdown.split(/\r?\n/)) {
    const m = line.match(/^\s*[-*•]\s+(.+)$/);
    if (!m || m[1].trim().startsWith("|")) continue;
    const clean = m[1]
      .replace(/\*\*(.+?)\*\*/g, "$1")
      .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
      .trim();
    if (clean.length < 20) continue;
    out.push(clean.length > 190 ? `${clean.slice(0, 187)}…` : clean);
    if (out.length >= max) break;
  }
  return out;
}

export interface AuditDatasetInfo {
  rango: string | null;
  total: number | null;
  objetivo: string | null;
  tablas: string | null;
  hitos?: string | null;
}

/** Lee del `dataset` guardado con la auditoría lo que Gertrudis recibió: ventana, volumen, objetivo y tablas. */
export function parseAuditDataset(dataset?: string | null): AuditDatasetInfo | null {
  if (!dataset) return null;
  const pick = (re: RegExp) => dataset.match(re)?.[1]?.trim() ?? null;
  const total = pick(/Total de comentarios únicos analizados:\s*(\d+)/);
  const tablas = pick(/<tablas_calculadas>([\s\S]*?)<\/tablas_calculadas>/);
  const hitos = pick(/(Hitos y eventos de gestión del cliente[\s\S]*?)(?=(?:Rango de fechas|<tablas_calculadas>))/);
  const info: AuditDatasetInfo = {
    rango: pick(/Rango de fechas de los comentarios:\s*([^\n]+)/),
    total: total ? Number(total) : null,
    objetivo: pick(/Objetivo comercial \/ foco del cliente:\s*([^\n]+)/),
    tablas,
    hitos,
  };
  return info.rango || info.total !== null || info.tablas || info.hitos ? info : null;
}

// Soporta emojis estándar (Extended_Pictographic), banderas regionales, keycaps numéricos y dingbats/símbolos
const EMOJI_REGEX = /(?:\p{Extended_Pictographic}|[\u{1F1E6}-\u{1F1FF}]{2}|[0-9#*]\uFE0F?\u20E3|[\u2600-\u27BF])/u;

/** Detecta si un mensaje contiene al menos un emoji (incluyendo banderas, símbolos y keycaps) */
export function containsEmoji(message?: string | null): boolean {
  if (!message) return false;
  return EMOJI_REGEX.test(message);
}

